<template>
  <div class="relative inline-block">
    <div
      @click.stop="onSwitchClick"
      @keydown.enter.prevent="onSwitchClick"
      @keydown.space.prevent="onSwitchClick"
      class="flex items-center focus:outline-none focus:ring-2 focus:ring-alert rounded-md cursor-pointer"
      tabindex="0"
      role="button"
      :aria-label="$t('commonLabels.delete')"
    >
      <slot name="trigger">
        <span
          v-html="trashIcon"
          ref="trigger"
          :modelValue="modelValue"
          :readonly="true"
        ></span>
      </slot>
    </div>
    <Popover
      ref="popover"
      :target="popoverTarget"
      :show-close-icon="false"
      :class="[
        'shadow-lg rounded-t-2 p-0 overflow-hidden !m-0 rounded-t-[8px]',
        width,
        height,
      ]"
      @hide="onHide"
      @show="onShow"
    >
      <div ref="popoverContent">
        <div
          v-if="header"
          class="max-h-[35px] flex items-center justify-between bg-greyLight text-white px-3 py-4 rounded-t-[8px] rounded-b-[0]"
        >
          <span
            class="font-just font-medium text-[14px] leading-[100%] tracking-[0%]"
          >
            {{ header }}
          </span>
          <button
            class="border-none bg-transparent justify-center border-0 text-white text-xl cursor-pointer p-0 flex items-center"
            @click="closePopover"
            :aria-label="$t('accessibilityLabels.closeMenu')"
            v-html="cancelSvg"
          ></button>
        </div>

        <slot :close="closePopover">
          <template v-if="items && items.length">
            <ul class="list-none m-0 p-0">
              <!-- NOSONAR -->
              <li
                v-for="(item, idx) in items"
                :key="idx"
                :class="[
                  'flex items-center gap-3 px-3 py-4 cursor-pointer border-t-0 border-x-0 border-mutedStone text-base bg-white transition',
                  confirmingIdx !== null && confirmingIdx !== idx
                    ? 'text-gray-300 cursor-not-allowed'
                    : '',
                  item.danger ? 'text-[#a94442]' : '',
                  !item.disabled && confirmingIdx === null
                    ? 'hover:bg-gray-100'
                    : '',
                  idx === items.length - 1 ? '' : 'border-b border-solid',
                  item.class || '',
                  confirmingIdx === idx ? 'h-[71px]' : 'h-[62px]',
                ]"
                @click="() => handleItemClick(item, idx, closePopover)"
                :disabled="
                  item.disabled ||
                  (confirmingIdx !== null && confirmingIdx !== idx)
                "
                tabindex="0"
                @keydown.enter.prevent="() => handleItemClick(item, idx, closePopover)"
                @keydown.space.prevent="() => handleItemClick(item, idx, closePopover)"
              >
                <template v-if="confirmingIdx === idx">
                  <div class="flex flex-col w-full items-center cursor-default">
                    <span
                      class="font-just font-500 text-[16px] leading-[100%] tracking-[0%] text-alert mb-2"
                    >
                      {{ $t('commonLabels.areYouSure') }}
                    </span>
                    <div class="flex gap-2 w-full">
                      <button
                        style="background: rgba(138, 151, 152, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-softGray"
                        @click.stop="handleCancelConfirm"
                      >
                        {{ $t('commonLabels.noCancel') }}
                      </button>
                      <button
                        style="background: rgba(134, 51, 41, 0.1)"
                        class="flex-1 cursor-pointer h-[26px] py-1 px-2 font-just font-semibold text-[14px] rounded border-none leading-[100%] tracking-[0%] text-alert"
                        @click.stop="handleConfirm(idx, closePopover, item)"
                      >
                        {{ $t('commonLabels.yesDelete') }}
                      </button>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="flex items-center gap-3 w-full">
                    <template v-if="$slots['item-icon']">
                      <slot name="item-icon"></slot>
                    </template>
                    <template v-else-if="item.icon">
                      <span v-html="item.icon" class="w-5 h-5"></span>
                    </template>
                    <span
                      class="flex-1 text-btnActive font-just font-medium text-[16px] leading-[100%] tracking-[0%]"
                      data-test-id="clear-btn"
                    >
                      {{ item.label }}
                    </span>
                  </div>
                </template>
              </li>
            </ul>
          </template>
        </slot>
      </div>
    </Popover>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import Popover from "primevue/popover";
import { closeSvg, trashIcon } from "@/utils/commonSvg";
import { usePopoverConfirmation } from "@/composables/usePopoverConfirmation";

const props = defineProps({
  items: {
    type: Array,
    default: () => [],
  },
  header: {
    type: String,
    default: "",
  },
  modelValue: Boolean,
  width: {
    type: String,
    default: "272px",
  },
  height: {
    type: String,
    default: "auto",
  },
});

const emit = defineEmits(["update:modelValue"]);

const trigger = ref(null);

const {
  popover,
  popoverTarget,
  confirmingIdx,
  togglePopover,
  closePopover,
  onHide,
  handleItemClick,
  handleConfirm,
  handleCancelConfirm,
} = usePopoverConfirmation(trigger, emit);

const cancelSvg = computed(() =>
  closeSvg({ width: "24px", height: "24px", color: "white" })
);

function onSwitchClick(event) {
  if (props.modelValue) {
    emit("update:modelValue", false);
    return;
  }
  togglePopover(event);
}

const popoverContent = ref(null);

const onShow = async () => {
  await nextTick();
  setTimeout(() => {
    if (popoverContent.value) {
      const focusable = popoverContent.value.querySelector(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      if (focusable) {
        focusable.focus();
      }
    }
  }, 100);
};
</script>

<style>
.p-popover .p-popover-content {
  padding: 0;
}

.p-popover {
  border: 0px !important;
}
</style>
